/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import JoinGraph from './JoinGraph';
import MoserObjectUtils from './MoserObjectUtils';
import UsageType from './UsageType';
import Cardinality from './Cardinality';
import ReferenceResolver from './ReferenceResolver';
import PropertySetter from './PropertySetter';
import QsClassifierType from './QsClassifierType';
import MetricGeneratorSpec from './MetricGeneratorSpec';
import MetricDefinitionHierarchy from './MetricDefinitionHierarchy';
import MetricDefinitionHierarchyItem from './MetricDefinitionHierarchyItem';
import MetricRoleType from './MetricRoleType';
import BaseQueryItemType from './BaseQueryItemType';
import HighLevelDataType from './HighLevelDataType';
import UseSpecType from './UseSpecType';
import TransientSourceType from './TransientSourceType';
import BaseMoserObject from './BaseMoserObject';
//@import com/ibm/bi/platform/moser/common/utils/JoinGraph (loadtime) // new, superclass
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getParentObjectOfType, containsUsage, getList, sortByLabel, getQueryItems, isHidden
//@import com/ibm/bi/platform/moser/common/generated/metadata/UsageType (static) // UsageType
//@import com/ibm/bi/platform/moser/common/utils/Cardinality (runtime) // new, Cardinality
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (runtime) // getFirstPart, getMoserObjectByIdForExpression
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (static) // getParentQuerySubject, isExtendedItem, isExtensionOfParentShortcut
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (runtime) // QsClassifierType
//@import com/ibm/bi/platform/moser/common/metric/hierarchy/MetricGeneratorSpec (runtime) // new
//@import com/ibm/bi/platform/moser/common/metric/hierarchy/MetricDefinitionHierarchy (runtime) // new
//@import com/ibm/bi/platform/moser/common/metric/hierarchy/MetricDefinitionHierarchyItem (runtime) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/MetricRoleType (static) // MetricRoleType
//@import com/ibm/bi/platform/moser/common/generated/metadata/BaseQueryItemType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/HighLevelDataType (static) // HighLevelDataType
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (runtime) // UseSpecType
//@import com/ibm/bi/platform/moser/common/generated/metadata/TransientSourceType (static) // TransientSourceType
//@import BaseMoserObject (loadtime) // superclass

/**
 * MetricJoinGraph is a utility class which consumes the join graph response to provide utility methods related to metrics. Used to generate a tree that recommends query items to include in a metric definition. Extends the JoinGraph class.
 * @author  alan-tran
 */
export default class MetricJoinGraph extends JoinGraph{

	//module = null;

	//relMap = null;

	//groupToQSMap = null;

	//useSpecIdToType = null;



	getModule() {
		return this.module;
	}

	/**
	 * Sets the module for the metric join graph. Cannot be done in constructor as the java to js does not translate subclass with constructors with different parameters then the super class.
	 * @param {Module} module
	 */
	setModule(module) {
		this.module = module;
		if (this.module) {
			this.relMap = this._createRelationshipMap();
			this.groupToQSMap = MetricJoinGraph._createGroupToQuerySubjectMap();
			this.useSpecIdToType = this._createUseSpecIdToUseSpecType();
		}
	}

	/**
	 * Returns a list of metric tree nodes which defines a tree structure representation of recommended query items to include in a metric definition. Returns an empty list if module is not defined.
	 * @param  initialQueryItems  list of query items
	 */
	getMetricDefinitionHierarchy$0(initialItems) {
		if (initialItems.length==0 || !this.getModule()) {
			return null;
		}
		return this._generateHierarchy(initialItems, null);
	}

	/**
	 * Returns the list of all query item ids from the join graph
	 */
	getJoinGraphQueryItemIds() {
		var queryItemIds = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.groupsMap._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			var groupQueryItemIds = group._getQueryItemsIDs();
			for (var __i_enFor1 = 0, __exp_enFor1 = groupQueryItemIds, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var id = __exp_enFor1[__i_enFor1];
				if (!(queryItemIds.indexOf(id)> -1)) {
					queryItemIds.push(id);
				}
			}
		}
		return queryItemIds;
	}

	/**
	 * Returns a list of related objects from the module to make a Metric Definition.
	 * @param {MoserObject} initialFact                A Moser object with UsageType.FACT
	 * @param {Set} seenFacts                  A set containing idForExpressions of measures already seen in the data module
	 * @param {int} distanceFromInitialGroup   The path length in the join graph away from the initial group for another group to be considered related. Ignored if -1 is passed.
	 * @param {int} distanceFromEdge           The max distance a group can be away from a leaf node in the join graph away to be considered related. Ignored if -1 is passed.
	 * @param {Set} factIncludeIds             The returned list of objects can only contain facts from this set. If not specified, all facts in the module can be included.
	 * @param {Set} attrIncludeIds             The returned list of objects can only contain attributes from this set. If not specified, all attributes in the module can be included.
	 */
	generateMetricSpec(initialFact, seenFacts, distanceFromInitialGroup, distanceFromEdge, factIncludeIds, attrIncludeIds) {
		if (!(MetricJoinGraph._isBaseQueryItemType(initialFact)) || !this.getModule() || !seenFacts) {
			return null;
		}
		var initialFactQueryItemType = initialFact;
		if (seenFacts.hasOwnProperty(initialFactQueryItemType.getIdForExpression()) || MoserObjectUtils.isHidden(initialFactQueryItemType, true) || !(MoserObjectUtils.containsUsage(initialFactQueryItemType, UsageType.FACT))) {
			return null;
		}
		if (factIncludeIds && !(factIncludeIds.hasOwnProperty(initialFactQueryItemType.getIdForExpression()))) {
			return null;
		}
		var initialGroupSet = this._findGroupSet$1(initialFactQueryItemType);
		if (initialGroupSet._isEmpty()) {
			seenFacts[initialFactQueryItemType.getIdForExpression()] = 1;
			return null;
		}
		var relatedGroups = this._getCombinedGroups(initialGroupSet, distanceFromInitialGroup, distanceFromEdge);
		if (!relatedGroups) {
			seenFacts[initialFactQueryItemType.getIdForExpression()] = 1;
			return null;
		}
		var initialGroup = initialGroupSet._getKeys()[0];
		var walker = new MetricJoinGraph.HeadDistanceWalker();
		walker._walkJoinGraphTraversal(initialGroup, Cardinality.MANY_TO_ONE, this);
		var traversalTree = walker._getTraversalPath();
		if (!traversalTree) {
			return null;
		}
		var traversalTrees = [];
		traversalTrees.push(traversalTree);
		var qsToNodeMap = {};
		var traversalPath = this._trimTraversalTree(traversalTrees, relatedGroups, qsToNodeMap);
		if (traversalPath.length==0) {
			return null;
		}
		var facts = [];
		var attributes = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getJoinGraphQueryItemIds(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var queryItemId = __exp_enFor0[__i_enFor0];
			var item = this.queryItemsMap[queryItemId];
			var idForExpression = item._getQueryItemID();
			if (!(seenFacts.hasOwnProperty(idForExpression))) {
				var itemGroup = item._getGroups();
				if (itemGroup.length==0) {
					continue;
				}
				var itemGroupSet = new JoinGraph.GroupSet();
				itemGroupSet._addAllList(itemGroup);
				var mo = ReferenceResolver.getMoserObjectByIdForExpression(idForExpression, this.getModule());
				if (!(MetricJoinGraph._isBaseQueryItemType(mo))) {
					continue;
				}
				var baseQueryItemType = mo;
				if (!(MoserObjectUtils.isHidden(baseQueryItemType, true))) {
					var parentQS = PropertySetter.getParentQuerySubject(baseQueryItemType);
					if (parentQS && QsClassifierType.METRIC_DEFINITION == parentQS.getClassifier()) {
						continue;
					}
					if (MoserObjectUtils.containsUsage(baseQueryItemType, UsageType.FACT)) {
						if (factIncludeIds && !(factIncludeIds.hasOwnProperty(idForExpression))) {
							continue;
						}
						var relatedGroupsFromWalkers = this._getCombinedGroups(itemGroupSet, distanceFromInitialGroup, distanceFromEdge);
						if (relatedGroups._containsAll(relatedGroupsFromWalkers) && relatedGroupsFromWalkers._containsAll(relatedGroups)) {
							seenFacts[idForExpression] = 1;
							facts.push(baseQueryItemType);
						}
					} else if (MoserObjectUtils.containsUsage(baseQueryItemType, UsageType.ATTRIBUTE)) {
						if (attrIncludeIds && !(attrIncludeIds.hasOwnProperty(idForExpression))) {
							continue;
						}
						if (relatedGroups._containsAll(itemGroupSet)) {
							if (parentQS && qsToNodeMap.hasOwnProperty(parentQS.getIdentifier())) {
								var qsNode = qsToNodeMap[parentQS.getIdentifier()];
								qsNode.addAttribute(baseQueryItemType);
							} else {
								attributes.push(baseQueryItemType);
							}
						}
					}
				}
			}
		}
		return new MetricGeneratorSpec(facts, attributes, traversalPath);
	}

	/**
	 * NOTE: Only used for testing purposes.
	 * @param {Array} objs
	 */
	convertToHierarchy(objs) {
		var hierarchy = new MetricDefinitionHierarchy();
		var itemList = [];
		hierarchy.setMetricDefinitionHierarchyItemList(itemList);
		for (var __i_enFor0 = 0, __exp_enFor0 = objs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			var item = new MetricDefinitionHierarchyItem(obj);
			itemList.push(item);
		}
		return hierarchy;
	}

	/**
	 * Returns a list of metric tree nodes which defines a tree structure representation of recommended query items to include in an existing metric definition. It will use the references of the actuals in a metric definition as the starting point. Returns an empty list if module is not defined.
	 * @param {QuerySubject} metricDefinition
	 */
	getMetricDefinitionHierarchy$1(metricDefinition) {
		if (!this.getModule()) {
			return null;
		}
		var queryItems = MoserObjectUtils.getQueryItems(metricDefinition);
		var baseQueryItemToMetricItemMap = this._createBaseQIToMetricItemMap(queryItems);
		var hierarchy = this._generateHierarchyForEdit(metricDefinition, queryItems, baseQueryItemToMetricItemMap);
		hierarchy.setTimeReference(metricDefinition.basicGetTimeRef());
		hierarchy.setTimeAnchor(metricDefinition.getTimeAnchor());
		hierarchy.setTimeGrain(metricDefinition.basicGetTimeGrain());
		return hierarchy;
	}

	/**
	 * @param {Array} initialFacts
	 * @param  refQIToMetricRole
	 * @return {MetricDefinitionHierarchy}
	 */
	_generateHierarchy(initialFacts, qiToMetricItemMap) {
		var outputTree = new MetricDefinitionHierarchy();
		var outputTreeItems = [];
		outputTree.setMetricDefinitionHierarchyItemList(outputTreeItems);
		outputTree.setInitialQueryItem(initialFacts);
		var traversalTrees = this._getTraversalTree$0(initialFacts);
		var allVisitedGroups = MetricJoinGraph._getGroupSetFromTraversalGroupNode(traversalTrees);
		var sharedGroupSet = MetricJoinGraph._getRelatedGroups(allVisitedGroups);
		var singlePath = this._trimTraversalTree(traversalTrees, sharedGroupSet, {});
		if (singlePath.length==0) {
			return outputTree;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = initialFacts, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			var qiNode = new MetricDefinitionHierarchyItem(qi);
			qiNode.setMetricRoleType(MetricRoleType.ACTUAL);
			outputTreeItems.push(qiNode);
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = singlePath, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var node = __exp_enFor1[__i_enFor1];
			var treeNode = this._visitTraversalNode(node, qiToMetricItemMap, null);
			if (treeNode) {
				outputTreeItems.push(treeNode);
			}
		}
		this.addCalcToHierarchy(outputTreeItems, sharedGroupSet, null, null);
		return outputTree;
	}

	/**
	 * @param {QuerySubject} metricDefinition
	 * @param  initialFacts
	 * @param {Array} metricQIs
	 * @param {Map} baseQueryItemToMetricItemMap
	 * @return {MetricDefinitionHierarchy}
	 */
	_generateHierarchyForEdit(metricDefinition, metricQIs, baseQueryItemToMetricItemMap) {
		var outputTree = new MetricDefinitionHierarchy();
		var outputTreeItems = [];
		outputTree.setMetricDefinitionHierarchyItemList(outputTreeItems);
		var initialFacts = [];
		var nonInitialFacts = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = metricQIs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			if (MetricJoinGraph._isValidInitialFact(qi)) {
				initialFacts.push(qi);
			} else if (MoserObjectUtils.containsUsage(qi, UsageType.FACT)) {
				nonInitialFacts.push(qi);
			}
		}
		var initialQueryItemList = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = initialFacts, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qi = __exp_enFor1[__i_enFor1];
			initialQueryItemList.push(qi);
		}
		outputTree.setInitialQueryItem(initialQueryItemList);
		var traversalTrees = this._getTraversalTree$0(initialQueryItemList);
		var allVisitedGroups = MetricJoinGraph._getGroupSetFromTraversalGroupNode(traversalTrees);
		var sharedGroupSet = MetricJoinGraph._getRelatedGroups(allVisitedGroups);
		var singlePath = this._trimTraversalTree(traversalTrees, sharedGroupSet, {});
		if (singlePath.length==0) {
			return outputTree;
		}
		for (var __i_enFor2 = 0, __exp_enFor2 = initialFacts, __len_enFor2 = __exp_enFor2.length;
				__i_enFor2 < __len_enFor2; ++__i_enFor2) {
			var qi = __exp_enFor2[__i_enFor2];
			var qiNode = MetricJoinGraph._createHierarchyItemFromFact(qi);
			outputTreeItems.push(qiNode);
		}
		var metricQIInHierarchySet = {};
		for (var __i_enFor3 = 0, __exp_enFor3 = singlePath, __len_enFor3 = __exp_enFor3.length;
				__i_enFor3 < __len_enFor3; ++__i_enFor3) {
			var node = __exp_enFor3[__i_enFor3];
			var treeNode = this._visitTraversalNode(node, baseQueryItemToMetricItemMap, metricQIInHierarchySet);
			if (treeNode) {
				outputTreeItems.push(treeNode);
			}
		}
		this.addCalcToHierarchy(outputTreeItems, sharedGroupSet, baseQueryItemToMetricItemMap, metricQIInHierarchySet);
		this.addExtraMetricItemsToHierarchy(outputTreeItems, metricDefinition, nonInitialFacts, metricQIInHierarchySet);
		return outputTree;
	}

	/**
	 * @param {Array} outputTreeItems
	 * @param {GroupSet} sharedGroupSet
	 * @param {Map} baseQueryItemToMetricItemMap
	 * @param {Set} metricQIInHierarchySet
	 */
	addCalcToHierarchy(outputTreeItems, sharedGroupSet, baseQueryItemToMetricItemMap, metricQIInHierarchySet) {
		var globalCalculations = MoserObjectUtils.getList(this.getModule().basicGetCalculation());
		var calcObjTypeList = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = globalCalculations, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			calcObjTypeList.push(obj);
		}
		var sortedGlobalCalcList = MoserObjectUtils.sortByLabel(calcObjTypeList);
		for (var __i_enFor1 = 0, __exp_enFor1 = sortedGlobalCalcList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var obj = __exp_enFor1[__i_enFor1];
			this._processCalculation(obj, sharedGroupSet, outputTreeItems, baseQueryItemToMetricItemMap, metricQIInHierarchySet);
		}
	}

	addExtraMetricItemsToHierarchy(outputTreeItems, metricDefinition, nonInitialFacts, metricQIInHierarchySet) {
		var qiChildrenNodes = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = nonInitialFacts, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			if (!(metricQIInHierarchySet.hasOwnProperty(qi.getIdentifier()))) {
				qiChildrenNodes.push(MetricJoinGraph._createHierarchyItemFromFact(qi));
			}
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(metricDefinition), __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qi = __exp_enFor1[__i_enFor1];
			if (!(metricQIInHierarchySet.hasOwnProperty(qi.getIdentifier()))) {
				MetricJoinGraph._processQueryItem(qi, qiChildrenNodes, false, false, null, null, null, null);
			}
		}
		if (!(qiChildrenNodes.length==0)) {
			var metricNode = new MetricDefinitionHierarchyItem(metricDefinition);
			this._processChildrenNodes(metricNode, qiChildrenNodes);
			outputTreeItems.push(metricNode);
		}
	}

	static _createHierarchyItemFromFact(qi) {
		var qiNode = new MetricDefinitionHierarchyItem(qi);
		if (!qi.getMetricRole()) {
			qiNode.setMetricRoleType(MetricRoleType.ACTUAL);
		} else {
			qiNode.setMetricRoleType(qi.getMetricRole());
		}
		qiNode.setMetricSentiment(qi.getMetricSentiment());
		if (MetricRoleType.TARGET == qi.getMetricRole()) {
			var parentQI = MoserObjectUtils.getParentObjectOfType(qi, "QueryItem");
			if (parentQI) {
				qiNode.setRelatedRef(parentQI);
			}
		}
		return qiNode;
	}

	/**
	 * @param {GroupSet} initialGroupSet
	 * @param {int} distanceFromInitialGroup
	 * @param {int} edgeDistance
	 * @return {GroupSet}
	 */
	_getCombinedGroups(initialGroupSet, distanceFromInitialGroup, edgeDistance) {
		var groupsFromWalkers = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = initialGroupSet._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var startGroup = __exp_enFor0[__i_enFor0];
			groupsFromWalkers.push(this._getGroupsFromDistanceWalkers(startGroup, distanceFromInitialGroup, edgeDistance));
		}
		return MetricJoinGraph._getRelatedGroups(groupsFromWalkers);
	}

	/**
	 * @param {Group} startGroup
	 * @param {int} distanceFromInitialGroup
	 * @param {int} edgeDistance
	 * @return {GroupSet}
	 */
	_getGroupsFromDistanceWalkers(startGroup, distanceFromInitialGroup, edgeDistance) {
		var walker = new MetricJoinGraph.HeadDistanceWalker();
		var seenWalkerSet = walker._walkJoinGraph(startGroup, Cardinality.MANY_TO_ONE, this);
		var filteredWalkerSet = new JoinGraph.GroupSet();
		if (distanceFromInitialGroup < 0) {
			filteredWalkerSet = seenWalkerSet;
		} else {
			for (var __i_enFor0 = 0, __exp_enFor0 = seenWalkerSet._getKeys(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var sgroup = __exp_enFor0[__i_enFor0];
				if (walker._getDistance(sgroup) >= distanceFromInitialGroup) {
					filteredWalkerSet._add(sgroup);
				}
			}
		}
		var tailWalker = new MetricJoinGraph.TailDistanceWalker();
		var seenTailWalkerSet = tailWalker._walkJoinGraph(startGroup, Cardinality.MANY_TO_ONE, this);
		var filteredTailSet = new JoinGraph.GroupSet();
		if (edgeDistance < 0) {
			filteredTailSet = seenTailWalkerSet;
		} else {
			for (var __i_enFor1 = 0, __exp_enFor1 = seenTailWalkerSet._getKeys(), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var sgroup = __exp_enFor1[__i_enFor1];
				if (tailWalker._getDistance(sgroup) <= edgeDistance) {
					filteredTailSet._add(sgroup);
				}
			}
		}
		var intersect = filteredWalkerSet._intersect(filteredTailSet);
		if (intersect._isEmpty()) {
			var subtractSelf = new JoinGraph.GroupSet();
			for (var __i_enFor2 = 0, __exp_enFor2 = filteredTailSet._getKeys(), __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var group = __exp_enFor2[__i_enFor2];
				if (!(group._getGroupID() == startGroup._getGroupID())) {
					subtractSelf._add(group);
				}
			}
			return subtractSelf;
		}
		return intersect;
	}

	static _isBaseQueryItemType(moserObject) {
		return moserObject instanceof BaseQueryItemType;
	}

	/**
	 * Returns a query subject associated with the group from the module
	 * @param {Group} group
	 */
	_getQuerySubjectListByGroup(group) {
		if (!(this.groupToQSMap.hasOwnProperty(group._getGroupID()))) {
			this._loadGroupToQuerySubjectMap(group);
		}
		return this.groupToQSMap[group._getGroupID()];
	}

	/**
	 * Creates a metric tree node to represent the internal traversal node. This is a post-order traversal. Meaning children are visited first before current node.
	 * @param {TraversalQSNode} traversalNode
	 * @param {Map} qiToMetricItemMap   map of already referenced attributes and their metric role.
	 * @param {Set} metricQIInHierarchySet
	 */
	_visitTraversalNode(traversalNode, qiToMetricItemMap, metricQIInHierarchySet) {
		var qs = traversalNode.getFirstQuerySubject();
		var childrenNodes = this._visitEdges(traversalNode.getChildren(), qiToMetricItemMap, metricQIInHierarchySet);
		var metricNode = new MetricDefinitionHierarchyItem(qs);
		this._processQSNode(metricNode, traversalNode, qiToMetricItemMap, metricQIInHierarchySet);
		var isQueryItemsEmpty = metricNode.getChildren().length==0;
		if (childrenNodes.length==0 && isQueryItemsEmpty) {
			return null;
		}
		this._processChildrenNodes(metricNode, childrenNodes);
		return metricNode;
	}

	/**
	 * Returns a list of child metric tree nodes for a given list of child traversal nodes. Note the size could differ. childrenNodes.size <= edges.size
	 * @param {Array} edges               list of children traversal nodes
	 * @param {Map} qiToMetricItemMap   map of already referenced attributes and their metric role.
	 * @param {Set} metricQIInHierarchySet
	 */
	_visitEdges(edges, qiToMetricItemMap, metricQIInHierarchySet) {
		var childrenNodes = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = edges, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var childNode = __exp_enFor0[__i_enFor0];
			var childMetricNode = this._visitTraversalNode(childNode, qiToMetricItemMap, metricQIInHierarchySet);
			if (childMetricNode) {
				childrenNodes.push(childMetricNode);
			}
		}
		return childrenNodes;
	}

	/**
	 * Populates a query subject node with query item nodes.
	 * @param {MetricDefinitionHierarchyItem} qsNode
	 * @param {TraversalQSNode} traversalQSNode
	 * @param {Map} qiToMetricItemMap    map of already referenced attributes and their metric role.
	 * @param {Set} metricQIInHierarchySet
	 */
	_processQSNode(qsNode, traversalQSNode, qiToMetricItemMap, metricQIInHierarchySet) {
		var qs = qsNode.getRef();
		var qiIdentifierMap = traversalQSNode.getAllValidQIIdentifiers();
		var qiIdToScIdForExprMap = traversalQSNode.createQIIToAllIdForExprMap();
		var qiIdentifierSet = qiIdentifierMap[qs.getIdentifier()];
		var qiNodes = [];
		MetricJoinGraph._processExtendedItem(traversalQSNode, qiNodes, qiIdentifierMap, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
		this._processItem(qs.basicGetItem(), qiNodes, false, qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
		for (var __i_enFor0 = 0, __exp_enFor0 = qiNodes, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qiNode = __exp_enFor0[__i_enFor0];
			qsNode.addChildren(qiNode);
		}
	}

	/**
	 * Iterates over all query items in a list of items and will create query item nodes to add to the list of qi nodes
	 * @param {Array} items                list of ItemType
	 * @param {Array} qiNodes              list of qi nodes that are part of a qs node
	 * @param {boolean} isParentHidden       the object containing the list of items is marked as hidden
	 * @param {Set} qiIdentifierSet      set of valid query item identifiers
	 * @param {Map} qiToMetricItemMap    map of already referenced attributes and their metric role.
	 * @param {Map} qiIdToScIdForExprMap
	 * @param {Set} metricQIInHierarchySet
	 */
	_processItem(items, qiNodes, isParentHidden, qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet) {
		if (!items) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (item.getQueryItem()) {
				MetricJoinGraph._processQueryItem(item.getQueryItem(), qiNodes, isParentHidden || MoserObjectUtils.isHidden(item.getQueryItem(), false), true, qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
				this._processItem(item.getQueryItem().basicGetItem(), qiNodes, isParentHidden, qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
			} else if (item.getFolder()) {
				this._processItem(item.getFolder().basicGetItem(), qiNodes, isParentHidden || MoserObjectUtils.isHidden(item.getFolder(), false), qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
			}
		}
	}

	static _processExtendedItem(traversalQSNode, qiNodes, qiIdentifierMap, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet) {
		for (var __i_enFor0 = 0, __exp_enFor0 = traversalQSNode.getExtendedQueryItems(qiIdentifierMap), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var extendedQI = __exp_enFor0[__i_enFor0];
			var parentQS = PropertySetter.getParentQuerySubject(extendedQI);
			var qiIdentifierSet = qiIdentifierMap[parentQS.getIdentifier()];
			MetricJoinGraph._processQueryItem(extendedQI, qiNodes, MoserObjectUtils.isHidden(extendedQI, true), false, qiIdentifierSet, qiToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
		}
	}

	/**
	 * Creates a query item tree node to add to the list of query item nodes if the qi passes the rules to be included.
	 * @param {QueryItem} qi                       query item
	 * @param {Array} qiNodes                  list of qi nodes that are part of a qs node
	 * @param {boolean} isHidden                 boolean indicating the qi is hidden either directly or indirectly by parent
	 * @param {Set} qiIdentifierSet          set of valid query item identifiers
	 * @param {Map} baseQueryItemToMetricItemMap        map of already referenced attributes and their metric role.
	 * @param {Map} qiIdToScIdForExprMap
	 * @param {Set} metricQIInHierarchySet
	 */
	static _processQueryItem(qi, qiNodes, isHidden, ignoreExtendedQI, qiIdentifierSet, baseQueryItemToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet) {
		if (MetricJoinGraph._excludeQI(qi, qiIdentifierSet, ignoreExtendedQI)) {
			return;
		}
		if (baseQueryItemToMetricItemMap) {
			MetricJoinGraph._processBaseQueryItemTypeForEdit(qi, qiNodes, baseQueryItemToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet);
		} else {
			var qiNode = new MetricDefinitionHierarchyItem(qi);
			if (!isHidden) {
				if (MetricJoinGraph._isDataTypeDate(qi)) {
					qiNode.setMetricRoleType(MetricRoleType.TIME);
				} else {
					qiNode.setMetricRoleType(MetricRoleType.NONE);
				}
			}
			qiNodes.push(qiNode);
		}
	}

	_processCalculation(calc, sharedGroupSet, hierarchyItemNodes, baseQueryItemToMetricItemMap, metricQIInHierarchySet) {
		if (MetricJoinGraph._excludeCalc(calc)) {
			return;
		}
		var calcGroup = this._findGroupSet$1(calc);
		if (!(sharedGroupSet._containsAll(calcGroup))) {
			return;
		}
		if (baseQueryItemToMetricItemMap) {
			MetricJoinGraph._processBaseQueryItemTypeForEdit(calc, hierarchyItemNodes, baseQueryItemToMetricItemMap, null, metricQIInHierarchySet);
		} else {
			var itemNode = new MetricDefinitionHierarchyItem(calc);
			if (!(MoserObjectUtils.isHidden(calc, true)) && !(calcGroup._isEmpty())) {
				if (MetricJoinGraph._isDataTypeDate(calc)) {
					itemNode.setMetricRoleType(MetricRoleType.TIME);
				} else {
					itemNode.setMetricRoleType(MetricRoleType.NONE);
				}
			}
			hierarchyItemNodes.push(itemNode);
		}
	}

	static _processBaseQueryItemTypeForEdit(baseQueryItemObj, hierarchyItemNodes, baseQueryItemToMetricItemMap, qiIdToScIdForExprMap, metricQIInHierarchySet) {
		var allMetricItems = [];
		var metricItems = baseQueryItemToMetricItemMap[baseQueryItemObj.getIdForExpression()];
		if (metricItems) {
			Array.prototype.push.apply(allMetricItems, metricItems);
		}
		if (qiIdToScIdForExprMap && qiIdToScIdForExprMap.hasOwnProperty(baseQueryItemObj.getIdentifier())) {
			var idForExpressionList = qiIdToScIdForExprMap[baseQueryItemObj.getIdentifier()];
			for (var __i_enFor0 = 0, __exp_enFor0 = idForExpressionList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var idForExpr = __exp_enFor0[__i_enFor0];
				if (idForExpr == baseQueryItemObj.getIdForExpression()) {
					continue;
				}
				metricItems = baseQueryItemToMetricItemMap[idForExpr];
				if (metricItems) {
					Array.prototype.push.apply(allMetricItems, metricItems);
				}
			}
		}
		if (!(allMetricItems.length==0)) {
			for (var __i_enFor1 = 0, __exp_enFor1 = allMetricItems, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var baseQIType = __exp_enFor1[__i_enFor1];
				if (metricQIInHierarchySet) {
					metricQIInHierarchySet[baseQIType.getIdentifier()] = 1;
				}
				var hierarchyItemNode = new MetricDefinitionHierarchyItem(baseQIType);
				if (!baseQIType.getMetricRole()) {
					hierarchyItemNode.setMetricRoleType(MetricRoleType.NONE);
				} else {
					hierarchyItemNode.setMetricRoleType(baseQIType.getMetricRole());
				}
				hierarchyItemNode.setMetricSentiment(baseQIType.getMetricSentiment());
				hierarchyItemNodes.push(hierarchyItemNode);
			}
		} else {
			var qiNode = new MetricDefinitionHierarchyItem(baseQueryItemObj);
			hierarchyItemNodes.push(qiNode);
		}
	}

	/**
	 * Returns true if the high level data type of a query item is a date.
	 * @param  qi             query item
	 */
	static _isDataTypeDate(baseQueryItemType) {
		return HighLevelDataType.DATE == baseQueryItemType.getHighlevelDatatype();
	}

	/**
	 * Returns true if the query item should not be included in the metric tree Current rule: If query item is not an attribute then ignore. If query item is not part of the related item normalization group then ignore.
	 * @param {QueryItem} qi                       query item
	 * @param {Set} qiIdentifierSet          set of valid query item identifiers
	 */
	static _excludeQI(qi, qiIdentifierSet, ignoreExtendedQI) {
		if (qiIdentifierSet && !(qiIdentifierSet.hasOwnProperty(qi.getIdentifier()))) {
			return true;
		}
		if (!(MoserObjectUtils.containsUsage(qi, UsageType.ATTRIBUTE))) {
			return true;
		}
		return ignoreExtendedQI && PropertySetter.isExtendedItem(qi);
	}

	static _excludeCalc(calc) {
		return !(MoserObjectUtils.containsUsage(calc, UsageType.ATTRIBUTE));
	}

	static _isValidInitialFact(qi) {
		return MoserObjectUtils.containsUsage(qi, UsageType.FACT) && !(MoserObjectUtils.isHidden(qi, true)) && (MetricRoleType.ACTUAL == qi.getMetricRole() || MetricRoleType.TARGET == qi.getMetricRole() || !qi.getMetricRole());
	}

	/**
	 * Adds the list of query subject children nodes to the current query subject node. Sets the relationship ref field in the children by finding the matching relationship in the module.
	 * @param {MetricDefinitionHierarchyItem} current         current query subject node
	 * @param {Array} childrenNodes   list of children query subject nodes
	 */
	_processChildrenNodes(current, childrenNodes) {
		for (var __i_enFor0 = 0, __exp_enFor0 = childrenNodes, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var child = __exp_enFor0[__i_enFor0];
			var rel = this._getRelationship(current.getRef(), child.getRef());
			if (rel) {
				child.setRelationshipRef(rel);
			}
			current.addChildren(child);
		}
	}

	/**
	 * Maps query items to its corresponding group. Creates a tree by traverses the join graph through N-1 and 1-1 connectors. This tree is used to help generate a list of MetricTreeNodes.
	 * @param  qiList                        List of query item
	 * @return {Array}   		                    Returns a list of containing a list of paths for each unique group found for each query item id.
	 * @throws {Error} IllegalArgumentException 		ID is not found or accessible;
	 */
	_getTraversalTree$0(objTypeList) {
		var groups = this._findGroupSet$0(objTypeList);
		var output = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = groups._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var g = __exp_enFor0[__i_enFor0];
			var root = this._getTraversalTree$1(g);
			output.push(root);
		}
		return output;
	}

	_getTraversalTree$1(group) {
		var seenSet = new JoinGraph.GroupSet();
		var root = new MetricJoinGraph.TraversalGroupNode(group);
		root.setVisitedGroups(seenSet);
		seenSet._add(group);
		this._getRelatedExplore$0(root, seenSet, Cardinality.MANY_TO_ONE);
		return root;
	}

	/**
	 * @param {Array} objTypeList
	 * @return {GroupSet}
	 */
	_findGroupSet$0(objTypeList) {
		var allGroupsSet = new JoinGraph.GroupSet();
		for (var __i_enFor0 = 0, __exp_enFor0 = objTypeList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var objType = __exp_enFor0[__i_enFor0];
			allGroupsSet._addAll(this._findGroupSet$1(objType));
		}
		return allGroupsSet;
	}

	/**
	 * Given an object, find its belonging group in the join graph.
	 * @param {ObjectType} obj
	 * @return {GroupSet}  GroupSet
	 */
	_findGroupSet$1(obj) {
		var groupSet = new JoinGraph.GroupSet();
		if (this.queryItemsMap.hasOwnProperty(obj.getIdForExpression())) {
			var groups = this.queryItemsMap[obj.getIdForExpression()]._getGroups();
			groupSet._addAllList(groups);
		} else {
			throw new Error("ID is not found or accessible: " + obj.getIdForExpression());
		}
		return groupSet;
	}

	/**
	 * Populates a traversal node by visiting all connecting groups using connections in the provided cardinality.
	 * @param {TraversalGroupNode} current         current group visiting
	 * @param {GroupSet} relatedGroups   set of groups related to current group
	 * @param {Cardinality} cardinality     cardinality to traverse
	 */
	_getRelatedExplore$0(current, relatedGroups, cardinality) {
		var adjacentGroups = this._getConnections(current.getGroup(), cardinality);
		for (var __i_enFor0 = 0, __exp_enFor0 = adjacentGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var right = __exp_enFor0[__i_enFor0];
			if (!(relatedGroups._contains(right))) {
				relatedGroups._add(right);
				var newNode = new MetricJoinGraph.TraversalGroupNode(right);
				current.addChild(newNode);
				this._getRelatedExplore$0(newNode, relatedGroups, cardinality);
			}
		}
	}

	/**
	 * Returns a Tree with only shared dimensions as nodes. Item normalization groups are associated with their QS. This trims the tree i.e QS_A_group 1 -> QS_A_group 2 -> QS_A_group3 -> QS_B = QS_A -> QS_B
	 * @param {Array} traversalTree
	 * @param {GroupSet} sharedGroupSet  Set of identifiers related to ALL groups by N-1 or 1-1
	 * @param {Map} qsIdToNode
	 */
	_trimTraversalTree(traversalTree, sharedGroupSet, qsIdToNode) {
		if (sharedGroupSet._isEmpty()) {
			return [];
		}
		var output = [];
		var root = traversalTree[0];
		var visitedGroupSet = new JoinGraph.GroupSet();
		this._populateTraversalQSTree(root, output, null, sharedGroupSet, visitedGroupSet, qsIdToNode);
		return output;
	}

	/**
	 * Creates a TraversalQSNode for the current TraversalGroupNode node. Will ignore if the group has already been visited. If the group has not been visited but the QS has, then add the group as part of valid item normalization groups of the QS.
	 * @param {TraversalGroupNode} node              current node
	 * @param {Array} output
	 * @param {GroupSet} sharedGroupSet    all shared/related groups
	 * @param {GroupSet} visitedGroupSet   set of visited groups
	 * @param {Map} qsIdToNode        maps query subject identifier to a TraversalQSNode
	 */
	_populateTraversalQSTree(node, output, parentNode, sharedGroupSet, visitedGroupSet, qsIdToNode) {
		var group = node.getGroup();
		if (visitedGroupSet._contains(group)) {
			return;
		}
		visitedGroupSet._add(group);
		if (!(sharedGroupSet._contains(group))) {
			for (var __i_enFor0 = 0, __exp_enFor0 = node.getChildren(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var childNode = __exp_enFor0[__i_enFor0];
				this._populateTraversalQSTree(childNode, output, parentNode, sharedGroupSet, visitedGroupSet, qsIdToNode);
			}
			return;
		}
		var qsList = this._getQuerySubjectListByGroup(group);
		var nextParentNode;
		for (var __i_enFor1 = 0, __exp_enFor1 = qsList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qs = __exp_enFor1[__i_enFor1];
			var qsId = qs.getIdentifier();
			if (nextParentNode) {
				if (!(qsIdToNode.hasOwnProperty(qsId))) {
					qsIdToNode[qsId] = nextParentNode;
				}
				if (MetricJoinGraph._isItemNormGroup(group._getGroupID())) {
					nextParentNode.addItemNormGroup(group);
				}
			} else {
				if (qsIdToNode.hasOwnProperty(qsId)) {
					var qsNode = qsIdToNode[qsId];
					if (MetricJoinGraph._isItemNormGroup(group._getGroupID())) {
						qsNode.addItemNormGroup(group);
					}
					nextParentNode = qsNode;
				} else {
					var newNode = new MetricJoinGraph.TraversalQSNode(qsList);
					if (MetricJoinGraph._isItemNormGroup(group._getGroupID())) {
						newNode.addItemNormGroup(group);
					}
					qsIdToNode[qsId] = newNode;
					if (!parentNode) {
						output.push(newNode);
					} else {
						parentNode.addChild(newNode);
					}
					nextParentNode = newNode;
				}
			}
		}
		if (nextParentNode) {
			for (var __i_enFor2 = 0, __exp_enFor2 = node.getChildren(), __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var childNode = __exp_enFor2[__i_enFor2];
				this._populateTraversalQSTree(childNode, output, nextParentNode, sharedGroupSet, visitedGroupSet, qsIdToNode);
			}
		}
	}

	static _isItemNormGroup(groupID) {
		return groupID.split(new RegExp("-")).length > 1;
	}

	static _copyGroupSetList(groupSetList) {
		var output = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = groupSetList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var groupSet = __exp_enFor0[__i_enFor0];
			var visitedGroupsCopy = new JoinGraph.GroupSet();
			visitedGroupsCopy._addAll(groupSet);
			output.push(visitedGroupsCopy);
		}
		return output;
	}

	/**
	 * Returns set of group identifiers that have been visited in ALL paths. This returns a set of shared groups.
	 * @param {Array} visitedGroupsList
	 */
	static _getRelatedGroups(visitedGroupsList) {
		var listToProcess = MetricJoinGraph._copyGroupSetList(visitedGroupsList);
		var isUnified = true;
		while (isUnified) {
			isUnified = false;
			for (var i = 0; i < listToProcess.length - 1; ++i) {
				for (var j = i + 1; j < listToProcess.length; ++j) {
					var groupSet1 = listToProcess[i];
					var groupSet2 = listToProcess[j];
					if (groupSet1._containsAll(groupSet2)) {
						isUnified = true;
						groupSet2._clear();
					} else if (groupSet2._containsAll(groupSet1)) {
						isUnified = true;
						groupSet1._clear();
					}
				}
			}
			if (isUnified) {
				var nextList = [];
				for (var __i_enFor0 = 0, __exp_enFor0 = listToProcess, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var visitedGroups = __exp_enFor0[__i_enFor0];
					if (!(visitedGroups._isEmpty())) {
						nextList.push(visitedGroups);
					}
				}
				listToProcess = nextList;
			}
		}
		var remainingList = listToProcess;
		var relatedGroups;
		for (var __i_enFor1 = 0, __exp_enFor1 = remainingList, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var visitedGroups = __exp_enFor1[__i_enFor1];
			if (!relatedGroups) {
				relatedGroups = visitedGroups;
			} else {
				relatedGroups = relatedGroups._intersect(visitedGroups);
			}
		}
		if (!relatedGroups) {
			relatedGroups = new JoinGraph.GroupSet();
		}
		return relatedGroups;
	}

	static _getGroupSetFromTraversalGroupNode(traversalTree) {
		var output = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = traversalTree, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var root = __exp_enFor0[__i_enFor0];
			if (root.getVisitedGroups()) {
				output.push(root.getVisitedGroups());
			}
		}
		return output;
	}

	static _createGroupToQuerySubjectMap() {
		return {};
	}

	_createUseSpecIdToUseSpecType() {
		var map = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getList(this.getModule().basicGetUseSpec()), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var useSpecObj = __exp_enFor0[__i_enFor0];
			var useSpec = useSpecObj;
			if (UseSpecType.PACKAGE == useSpec.getType()) {
				map[useSpec.getIdentifier()] = useSpec.getType();
			}
		}
		return map;
	}

	_loadGroupToQuerySubjectMap(group) {
		var seenQS = {};
		var qsList = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = group._getQueryItemsIDs(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var queryItemID = __exp_enFor0[__i_enFor0];
			if (this._isGroupLoaded(queryItemID, qsList)) {
				continue;
			}
			var mo = ReferenceResolver.getMoserObjectByIdForExpression(queryItemID, this.getModule());
			var parentQS = PropertySetter.getParentQuerySubject(mo);
			if (parentQS && !(seenQS.hasOwnProperty(parentQS.getIdentifier())) && QsClassifierType.METRIC_DEFINITION != parentQS.getClassifier()) {
				seenQS[parentQS.getIdentifier()] = 1;
				qsList.push(parentQS);
			}
		}
		var sorted = MoserObjectUtils.sortByLabel(qsList);
		var qsSorted = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = sorted, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var obj = __exp_enFor1[__i_enFor1];
			qsSorted.push(obj);
		}
		var finalQSList = MetricJoinGraph._removeDupShortcutsFromPackage(qsSorted);
		this.groupToQSMap[group._getGroupID()] = finalQSList;
	}

	_isGroupLoaded(queryItemID, qsList) {
		var firstPart = ReferenceResolver.getFirstPart(queryItemID);
		var isMultiPartId = !("" == firstPart);
		if (!isMultiPartId) {
			return false;
		}
		var isFromPackage = this._isFromPackage(firstPart);
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var isParentQSFromPckLoaded = isFromPackage && TransientSourceType.PACKAGE == qs.getTransientSource() && queryItemID.startsWith(qs.getIdentifier());
			var isParentQSFromModule = !isFromPackage && !qs.getTransientSource() && firstPart == qs.getIdForExpression();
			if (isParentQSFromPckLoaded || isParentQSFromModule) {
				return true;
			}
		}
		return false;
	}

	_isFromPackage(moduleAlias) {
		return this.useSpecIdToType.hasOwnProperty(moduleAlias);
	}

	static _removeDupShortcutsFromPackage(qsList) {
		var output = [];
		var scRefSet = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			if (TransientSourceType.PACKAGE == qs.getTransientSource()) {
				var identifier = qs.getIdentifier();
				var moduleAlias = ReferenceResolver.getFirstPart(identifier);
				var targetQualifiedId = null;
				if (qs.getShortcutTargetQuerySubjectRef() != null) {
					targetQualifiedId = moduleAlias + qs.getShortcutTargetQuerySubjectRef();
				} else {
					targetQualifiedId = qs.getIdentifier();
				}
				if (!(scRefSet.hasOwnProperty(targetQualifiedId))) {
					scRefSet[targetQualifiedId] = 1;
					output.push(qs);
				}
			} else {
				output.push(qs);
			}
		}
		return output;
	}

	_createBaseQIToMetricItemMap(metricQueryItems) {
		var map = {};
		var metricItemIdentifierSet = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = metricQueryItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			metricItemIdentifierSet[qi.getIdentifier()] = 1;
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = metricQueryItems, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qi = __exp_enFor1[__i_enFor1];
			if (MetricJoinGraph._excludeQI(qi, null, false)) {
				continue;
			}
			var expression = qi.getExpression().trim();
			if (this.queryItemsMap.hasOwnProperty(expression)) {
				var isReference = !(metricItemIdentifierSet.hasOwnProperty(expression));
				if (!isReference) {
					isReference = qi.getIdentifier() == expression;
				}
				if (isReference) {
					if (map.hasOwnProperty(expression)) {
						map[expression].push(qi);
					} else {
						var metricItemsList = [];
						metricItemsList.push(qi);
						map[expression] = metricItemsList;
					}
				}
			}
		}
		return map;
	}

	_createRelationshipMap() {
		var map = {};
		var relList = MoserObjectUtils.getList(this.getModule().basicGetRelationship());
		for (var __i_enFor0 = 0, __exp_enFor0 = relList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var rel = __exp_enFor0[__i_enFor0];
			var left = rel.getLeft();
			var right = rel.getRight();
			var leftRef = left.getRef();
			var rightRef = right.getRef();
			if (map.hasOwnProperty(leftRef)) {
				var rightMap = map[leftRef];
				rightMap[rightRef] = rel;
			} else {
				var rightMap = {};
				rightMap[rightRef] = rel;
				map[leftRef] = rightMap;
			}
			if (map.hasOwnProperty(rightRef)) {
				var leftMap = map[rightRef];
				leftMap[leftRef] = rel;
			} else {
				var leftMap = {};
				leftMap[leftRef] = rel;
				map[rightRef] = leftMap;
			}
		}
		return map;
	}

	/**
	 * Returns the first relationship in the module joining the two query subjects. Returns null if matching relationship is not found.
	 * @param {ObjectType} leftQS    query subject
	 * @param {ObjectType} rightQS   query subject
	 */
	_getRelationship(leftQS, rightQS) {
		var qsRightToRelationship = this.relMap[leftQS.getIdentifier()];
		if (!qsRightToRelationship) {
			return null;
		}
		return qsRightToRelationship[rightQS.getIdentifier()];
	}

	getMetricDefinitionHierarchy(a0) {
		var args = arguments;
		if (args.length == 1 && (a0 == null || Object.prototype.toString.call(a0) === "[object Array]")) {
			return this.getMetricDefinitionHierarchy$0(a0);
		}
		return this.getMetricDefinitionHierarchy$1(a0);
	}

	getTraversalTree(a0) {
		var args = arguments;
		if (args.length == 1 && (a0 == null || Object.prototype.toString.call(a0) === "[object Array]")) {
			return this.getTraversalTree$0(a0);
		}
		return this.getTraversalTree$1(a0);
	}

	findGroupSet(a0) {
		var args = arguments;
		if (args.length == 1 && (a0 == null || Object.prototype.toString.call(a0) === "[object Array]")) {
			return this.findGroupSet$0(a0);
		}
		return this.findGroupSet$1(a0);
	}

	getRelatedExplore(a0, a1, a2) {
		var args = arguments;
		if (args.length == 3 && (a0 == null || rave['internal']['Object'].isInstanceOf(a0, MetricJoinGraph.TraversalGroupNode)) && (a1 == null || rave['internal']['Object'].isInstanceOf(a1, JoinGraph.GroupSet)) && (a2 == null || rave['internal']['Object'].isInstanceOf(a2, Cardinality))) {
			return this.getRelatedExplore$0(a0, a1, a2);
		}
		return JoinGraph.prototype.getRelatedExplore.apply(this, args);
	}

	/**
	 * @param {Object} joinGraph
	 */
	//constructor(joinGraph) 
}
/**
 * A helper class to represent the results of traversing the JoinGraph. A TraversalGroupNode represents a group. If a TraversalNode is a root node, then it will contain a set of groups visited during traversal.
 * @author  AlanTran
 */
MetricJoinGraph.TraversalGroupNode = class extends BaseMoserObject{

	group = null;

	children = null;

	visitedGroups = null;

	parentNode = null;



	constructor(group) {
		super();
		this.group = group;
		this.children = [];
		this.setVisitedGroups(null);
	}

	/**
	 * @return {Group}  the group
	 */
	getGroup() {
		return this.group;
	}

	/**
	 * @return {Array}  the children
	 */
	getChildren() {
		return this.children;
	}

	/**
	 * @return {GroupSet}  the visitedGroups
	 */
	getVisitedGroups() {
		return this.visitedGroups;
	}

	/**
	 * @param {GroupSet} visitedGroups  the visitedGroups to set
	 */
	setVisitedGroups(visitedGroups) {
		this.visitedGroups = visitedGroups;
	}

	addChild(child) {
		this.children.push(child);
	}

	setParentNode(parent) {
		this.parentNode = parent;
	}

	getParentNode() {
		return this.parentNode;
	}

	toString() {
		return this.group.toString();
	}
}
/**
 * @author  AlanTran A helper class to represent the results of traversing the JoinGraph. A TraversalQSNode represents a QS. A TraversalQSNode can be associated to many Groups (item norm).
 */
MetricJoinGraph.TraversalQSNode = class extends BaseMoserObject{

	qsList = null;

	children = null;

	itemNormGroupSet = null;

	attrList = null;



	constructor(qsList) {
		super();
		this.qsList = qsList;
		this.children = [];
		this.setItemNormGroupSet(new JoinGraph.GroupSet());
		this.attrList = [];
	}

	/**
	 * @return {QuerySubject}  the qs
	 */
	getFirstQuerySubject() {
		return this.qsList[0];
	}

	getQSList() {
		return this.qsList;
	}

	/**
	 * @return {Array}  the children
	 */
	getChildren() {
		return this.children;
	}

	/**
	 * @return {GroupSet}  the itemNormGroupSet
	 */
	getItemNormGroupSet() {
		return this.itemNormGroupSet;
	}

	addItemNormGroup(itemNormGroup) {
		this.itemNormGroupSet._add(itemNormGroup);
	}

	/**
	 * @param {GroupSet} itemNormGroupSet  the itemNormGroupSet to set
	 */
	setItemNormGroupSet(itemNormGroupSet) {
		this.itemNormGroupSet = itemNormGroupSet;
	}

	addChild(child) {
		this.children.push(child);
	}

	addAttribute(item) {
		this.attrList.push(item);
	}

	getAttributes() {
		return this.attrList;
	}

	/**
	 * Returns a Set of query item identifiers that belong to the set of item normalization groups.
	 * @param  qs
	 * @param  itemNormGroupSet   All related item normalization groups
	 */
	getAllValidQIIdentifiers() {
		var output = {};
		if (this.getItemNormGroupSet()._isEmpty()) {
			return output;
		}
		var itemNormIdentifierSet = this._getItemNormGroupIdentifierSet();
		var qiIdentifierSet = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getQSList(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var itemNormalizationType = qs.getItemNormalization();
			if (!itemNormalizationType) {
				continue;
			}
			var itemNormGroups = MoserObjectUtils.getList(itemNormalizationType.basicGetItemNormalizationGroup());
			for (var __i_enFor1 = 0, __exp_enFor1 = itemNormGroups, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var group = __exp_enFor1[__i_enFor1];
				if (itemNormIdentifierSet.hasOwnProperty(group.getIdentifier())) {
					var key = group.getKey();
					if (key) {
						qiIdentifierSet[key.getItemRef()] = 1;
					}
					var attributes = MoserObjectUtils.getList(group.basicGetAttribute());
					for (var __i_enFor2 = 0, __exp_enFor2 = attributes, __len_enFor2 = __exp_enFor2.length;
							__i_enFor2 < __len_enFor2; ++__i_enFor2) {
						var attribute = __exp_enFor2[__i_enFor2];
						qiIdentifierSet[attribute.getItemRef()] = 1;
					}
				}
			}
			output[qs.getIdentifier()] = qiIdentifierSet;
		}
		return output;
	}

	/**
	 * Returns a Set of item normalization group group identifiers
	 * @param  itemNormSet
	 */
	_getItemNormGroupIdentifierSet() {
		var groupIDSet = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getItemNormGroupSet()._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			var groupID = group._getGroupID();
			groupIDSet[groupID.split(new RegExp("-"))[1]] = 1;
		}
		return groupIDSet;
	}

	createQIIToAllIdForExprMap() {
		if (this.getQSList().length <= 1) {
			return {};
		}
		var qiToAllIdForExprMap = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getQSList(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(qs), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var qi = __exp_enFor1[__i_enFor1];
				if (qiToAllIdForExprMap.hasOwnProperty(qi.getIdentifier())) {
					var idForExpr = qiToAllIdForExprMap[qi.getIdentifier()];
					idForExpr.push(qi.getIdForExpression());
				} else {
					var idForExpr = [];
					idForExpr.push(qi.getIdForExpression());
					qiToAllIdForExprMap[qi.getIdentifier()] = idForExpr;
				}
			}
		}
		return qiToAllIdForExprMap;
	}

	getExtendedQueryItems(qiIdentifierMap) {
		var sortedExtendedItem = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.getQSList(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var qiIdentifierSet = qiIdentifierMap[qs.getIdentifier()];
			for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getQueryItems(qs), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var qi = __exp_enFor1[__i_enFor1];
				if (PropertySetter.isExtensionOfParentShortcut(qs, qi)) {
					if (qiIdentifierSet && !(qiIdentifierSet.hasOwnProperty(qi.getIdentifier()))) {
						continue;
					}
					sortedExtendedItem.push(qi);
				}
			}
		}
		sortedExtendedItem = MoserObjectUtils.sortByLabel(sortedExtendedItem);
		var output = [];
		for (var __i_enFor2 = 0, __exp_enFor2 = sortedExtendedItem, __len_enFor2 = __exp_enFor2.length;
				__i_enFor2 < __len_enFor2; ++__i_enFor2) {
			var objType = __exp_enFor2[__i_enFor2];
			output.push(objType);
		}
		return output;
	}

	toString() {
		return this.getFirstQuerySubject().getIdentifier();
	}
}
/**
 * walking the joinGraph, starting with a particular group. Groups can be connected in a cirular fashion. Eg g1(N)-a-(1)g2(N)-b-(1)g3-\ | |                    |   \ | +(1)-----c--------(N)+   | |                          | +(N)-d-(1)g4(N)---e-----(1)+ | +(N)-f-(1)g5(N)-g-(1)g6(N)-h-(1)g7(N)-i-(1)g8 |                               | +(N)---------------j---------(1)+ traverse start at g2 g2                               g2:0 -b-g3                           g3:1 -c-g1                        g1:2 -a-g2 (onstack) -d-g4                     g4:3 -e-g3 (onstack) -f-g5                  g5:4 -g-g6               g6:5 -h-g7            g7:6 -i-g8 (leaf)  g8:7 -j  (wrong card) -j-g7 (seen)           g7:4   (this is shorter) -h (wrong card) -i-g8 (seen)        g8:5   (this is shorter) -e (wrong card) -a  (wrong card)
 * @author  AlanTran
 */
MetricJoinGraph.HeadDistanceWalker = class extends BaseMoserObject{

	//distance = null;

	//joinGraph = null;

	//path = null;



	stack = null;

	seenGroups = null;



	_init(graph) {
		this.stack = [];
		this.seenGroups = new JoinGraph.GroupSet();
		this.distance = {};
		this.joinGraph = graph;
		this.path = null;
	}

	_walkJoinGraph(start, cardinality, graph) {
		this._init(graph);
		this._walk(start, cardinality, 0, null, null);
		return this.seenGroups;
	}

	_walkJoinGraphTraversal(start, cardinality, graph) {
		this._init(graph);
		var groupNodeMap = {};
		this._walk(start, cardinality, 0, null, groupNodeMap);
		this.path = groupNodeMap[start];
		return this.seenGroups;
	}

	_walk(grp, cardinality, depth, parentNode, groupNodeMap) {
		if (this.stack.indexOf(grp)> -1) {
			return;
		}
		this.stack.push(grp);
		if (!(this.seenGroups._contains(grp))) {
			this.seenGroups._add(grp);
			this.distance[grp] = depth;
			if (groupNodeMap) {
				var node = new MetricJoinGraph.TraversalGroupNode(grp);
				groupNodeMap[grp] = node;
				if (parentNode) {
					parentNode.addChild(node);
					node.setParentNode(parentNode);
				}
				for (var __i_enFor0 = 0, __exp_enFor0 = this.joinGraph._getConnections(grp, cardinality), __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var cgroup = __exp_enFor0[__i_enFor0];
					this._walk(cgroup, cardinality, depth + 1, node, groupNodeMap);
				}
			} else {
				for (var __i_enFor1 = 0, __exp_enFor1 = this.joinGraph._getConnections(grp, cardinality), __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var cgroup = __exp_enFor1[__i_enFor1];
					this._walk(cgroup, cardinality, depth + 1, null, null);
				}
			}
		} else {
			var dist = this.distance[grp];
			if (dist > depth) {
				this.distance[grp] = depth;
				if (groupNodeMap) {
					var node = groupNodeMap[grp];
					var oldParent = node.getParentNode();
					if (oldParent && parentNode && oldParent != parentNode) {
						var childrenNodes = oldParent.getChildren();
						var removeIndex = childrenNodes.indexOf(node);
						if (removeIndex >= 0) {
							oldParent.getChildren().splice(removeIndex, 1);
						}
						parentNode.addChild(node);
						node.setParentNode(parentNode);
					}
					for (var __i_enFor2 = 0, __exp_enFor2 = this.joinGraph._getConnections(grp, cardinality), __len_enFor2 = __exp_enFor2.length;
							__i_enFor2 < __len_enFor2; ++__i_enFor2) {
						var cgroup = __exp_enFor2[__i_enFor2];
						this._walk(cgroup, cardinality, depth + 1, node, groupNodeMap);
					}
				} else {
					for (var __i_enFor3 = 0, __exp_enFor3 = this.joinGraph._getConnections(grp, cardinality), __len_enFor3 = __exp_enFor3.length;
							__i_enFor3 < __len_enFor3; ++__i_enFor3) {
						var cgroup = __exp_enFor3[__i_enFor3];
						this._walk(cgroup, cardinality, depth + 1, null, null);
					}
				}
			}
		}
		this.stack.splice(this.stack.length - 1, 1);
	}

	_getTraversalPath() {
		return this.path;
	}

	_getDistance(group) {
		return this.distance[group];
	}
}
/**
 * g1(N)---a---(1)g2(N)---b---(1)g3 | +(N)--c--(1)g4(N)--d--(1)g5 g2 when using b is 1 away from the edge(leaf) when using c and d it is 2 away from the edge(leaf) consolidated, g2 is 1 away from the edge and g1 is thus 2 away from the edge.
 * @author  AlanTran
 */
MetricJoinGraph.TailDistanceWalker = class extends BaseMoserObject{

	//distance = null;

	//joinGraph = null;



	stack = null;

	seenGroups = null;



	_init(graph) {
		this.stack = [];
		this.seenGroups = new JoinGraph.GroupSet();
		this.distance = {};
		this.joinGraph = graph;
	}

	_walkJoinGraph(start, cardinality, graph) {
		this._init(graph);
		this._walkInt(start, cardinality);
		return this.seenGroups;
	}

	_walkInt(grp, cardinality) {
		if (this.stack.indexOf(grp)> -1) {
			return -1;
		}
		this.stack.push(grp);
		if (!(this.seenGroups._contains(grp))) {
			this.seenGroups._add(grp);
			var cgrps = this.joinGraph._getConnections(grp, cardinality);
			if (cgrps.length==0) {
				this.distance[grp] = 0;
			} else {
				var dist = 0;
				for (var __i_enFor0 = 0, __exp_enFor0 = cgrps, __len_enFor0 = __exp_enFor0.length;
						__i_enFor0 < __len_enFor0; ++__i_enFor0) {
					var cgroup = __exp_enFor0[__i_enFor0];
					var cdist = this._walkInt(cgroup, cardinality);
					if (cdist < 0) {
						continue;
					}
					if (dist == 0 || cdist < dist) {
						dist = cdist;
					}
				}
				this.distance[grp] = dist + 1;
			}
		}
		this.stack.splice(this.stack.length - 1, 1);
		return this.distance[grp];
	}

	_getDistance(group) {
		return this.distance[group];
	}
}


// WEBPACK FOOTER //
// ./src/generated/MetricJoinGraph.js